import {
  SimpleGrid,
  Card,
  Flex,
  Center,
  Box,
  Text,
  Heading,
  Icon,
  Progress,
  Badge,
  HStack
} from '@chakra-ui/react';
import { FiLayers, FiCheckCircle, FiBarChart2, FiTarget } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

interface StatisticsCardsProps {
  totalProjects: number;
  totalTasks: number;
  completedTasks: number;
  completionRate: number;
  urgentTasks: number;
  vitalTasks: number;
  accentColor: string;
  subtleColor: string;
}

export const StatisticsCards = ({
  totalProjects,
  totalTasks,
  completedTasks,
  completionRate,
  urgentTasks,
  vitalTasks,
  accentColor,
  subtleColor
}: StatisticsCardsProps) => {
  const { t } = useTranslation(['v0/common', 'v0/me']);

  return (
    <SimpleGrid
      columns={{ base: 2, md: 4 }}
      spacing={{ base: 2, md: 3 }}
      mb={3}
    >
      <Card borderRadius="lg" overflow="hidden">
        <Flex alignItems="center" p={3}>
          <Center bg={subtleColor} borderRadius="full" p={2} mr={3}>
            <Icon as={FiLayers} color={accentColor} />
          </Center>
          <Box>
            <Text fontSize="sm" color="gray.500">{t('v0/me:stats.projects')}</Text>
            <Heading size="md">{totalProjects}</Heading>
          </Box>
        </Flex>
      </Card>

      <Card borderRadius="lg" overflow="hidden">
        <Flex alignItems="center" p={3}>
          <Center bg={subtleColor} borderRadius="full" p={2} mr={3}>
            <Icon as={FiCheckCircle} color={accentColor} />
          </Center>
          <Box>
            <Text fontSize="sm" color="gray.500">{t('v0/me:stats.tasks')}</Text>
            <Heading size="md">{totalTasks}</Heading>
            <Text fontSize="xs" color="gray.500">
              {completedTasks} {t('v0/common:common.completed')}
            </Text>
          </Box>
        </Flex>
      </Card>

      <Card borderRadius="lg" overflow="hidden">
        <Flex alignItems="center" p={3}>
          <Center bg={subtleColor} borderRadius="full" p={2} mr={3}>
            <Icon as={FiBarChart2} color={accentColor} />
          </Center>
          <Box>
            <Text fontSize="sm" color="gray.500">{t('v0/me:stats.completionRate')}</Text>
            <Heading size="md">{completionRate}%</Heading>
            <Progress
              value={completionRate}
              size="xs"
              colorScheme={completionRate > 70 ? 'green' : 'orange'}
              mt={1}
              borderRadius="full"
            />
          </Box>
        </Flex>
      </Card>

      <Card borderRadius="lg" overflow="hidden">
        <Flex alignItems="center" p={3}>
          <Center bg={subtleColor} borderRadius="full" p={2} mr={3}>
            <Icon as={FiTarget} color={accentColor} />
          </Center>
          <Box>
            <Text fontSize="sm" color="gray.500">{t('v0/me:stats.priorityTasks')}</Text>
            <Heading size="md">{urgentTasks + vitalTasks}</Heading>
            <HStack spacing={1} mt={1}>
              {urgentTasks > 0 && (
                <Badge colorScheme="orange" variant="subtle">{urgentTasks} {t('v0/me:stats.urgent')}</Badge>
              )}
              {vitalTasks > 0 && (
                <Badge colorScheme="red" variant="subtle">{vitalTasks} {t('v0/me:stats.important')}</Badge>
              )}
            </HStack>
          </Box>
        </Flex>
      </Card>
    </SimpleGrid>
  );
}; 